import config from "../../../config";
import React, {useEffect, useState} from "react";
import {FormControl, Input, TableContainer} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableFooter from "@material-ui/core/TableFooter";
import {run} from "../../../utils/function-util";
import TablePager from "../pagination/TablePager";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import {makeStyles} from "@material-ui/core/styles/index";
import useDebounce from "../../_shared/useDebounce";
import {useApiNotification} from "../ApiNotificationProvider";

const useStyles = makeStyles(theme => ({
    search: {
        [theme.breakpoints.down('sm')]: {
            width: '100%'
        },
        [theme.breakpoints.up('md')]: {
            width: '40%'
        }
    }
}));


export const withLazyDynamicTable = (
    WrappedComponent: any,
    query: any,
    dependencies: any,
    TableOption?: any
) => ({
          refresh,
          ...rest
      }: any) => {
    const classes = useStyles();
    const [filter, setFilter] = useState('');
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(config.uiConfig.defaultRowsPerPage);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const debouncedFilter = useDebounce(filter, 300);
    const {handleError} = useApiNotification();

    const loadData = async (pageToLoad: any, size: any, filterToLoad: any) => {
        setLoading(true);
        const searchArgs = typeof (refresh) !== "number" ? {size, page: pageToLoad, filter: filterToLoad, ...refresh} : {size, page: pageToLoad, filter: filterToLoad};
        return query({...searchArgs});
    };

    useEffect(run(() =>
            loadData(0, pageSize, debouncedFilter),
        (values: any) => {
            if (page > 0)
                setPage(0);

            setTotal(values.total);
            setData(values.items);
        },
        handleError,
        () => setLoading(false)
    ), [...dependencies.map((d: any) => rest[d]), debouncedFilter, pageSize]);

    useEffect(() => {
        if (refresh)
            return run(() => loadData(page, pageSize, debouncedFilter),
                (values: any) => {
                    setTotal(values.total);
                    setData(values.items);
                },
                handleError,
                () => setLoading(false)
            )()
    }, [refresh]);

    const handleItemUpdated = (item: any, compareFn: any) => {
        if (!item || !compareFn) return;
        setData((dataToUpdate: any) => {
            const itemIdx = dataToUpdate.findIndex(compareFn);
            if (itemIdx < 0) return dataToUpdate;
            const updatedData: any[] = [...dataToUpdate];
            updatedData[itemIdx] = item;
            return updatedData;
        });
    }

    const handlePageChanged = async (event: any, newPage: any) => {
        setPage(newPage);
        const values = await loadData(newPage, pageSize, debouncedFilter);
        setTotal(values.total);
        setData(values.items);
        setLoading(false);
    };

    const handlePageSizeChanged = (event: any) => {
        setPageSize(parseInt(event.target.value, 10));
    };

    const handleFilterChange = (value: any) => {
        setFilter(value);
    };

    return (<Grid container spacing={3}>
        <Grid item xs={12}>
            <FormControl className={classes.search}>
                <Input value={filter}
                       onChange={e => handleFilterChange(e.target.value)}
                       startAdornment={
                           <InputAdornment position="start">
                               <SearchIcon/>
                           </InputAdornment>
                       }
                       endAdornment={!!filter ?
                           <InputAdornment position="start" style={{cursor: 'pointer'}}>
                               <ClearIcon onClick={() => handleFilterChange('')}/>
                           </InputAdornment> : undefined
                       }/>
            </FormControl>
        </Grid>
        {TableOption}
        <Grid item xs={12}>
            <TableContainer>
                <Table>
                    <WrappedComponent {...rest} data={data} onDataItemUpdated={handleItemUpdated} loading={loading}/>
                    <TableFooter>
                        <TablePager count={total}
                                    rowsPerPage={pageSize}
                                    handleChangePage={handlePageChanged}
                                    page={page}
                                    handleChangeRowsPerPage={handlePageSizeChanged}/>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Grid>
    </Grid>);
};
